/**
 * JobWorkflowModal.js - Complete job workflow for workers
 * Handles: Clock in (GPS) → Work details/Checklist → Photos → Signature → Clock out
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MapPin, Clock, CheckCircle, Camera, Pen, Upload, X, ChevronRight,
  AlertTriangle, Home, Key, User, Phone, FileText, Timer, Check,
  Image, MessageSquare, Send, Loader2, CheckSquare, Square, Navigation,
  ClipboardList, Calendar, DollarSign, Info, Lock, Users, Banknote, XCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import api from '../../services/api';
import BeforeAfterPhotoUpload from './BeforeAfterPhotoUpload';
import CourierWorkflowSteps from './CourierWorkflowSteps';

const WORKFLOW_STEPS = {
  CASH_VERIFY: 'cash_verify',  // New step for cash payment verification
  CLOCK_IN: 'clock_in',
  GPS_CHECK: 'gps_check',
  WORK_DETAILS: 'work_details',
  IN_PROGRESS: 'in_progress',
  COMPLETION: 'completion',
  PHOTOS: 'photos',
  NOTES: 'notes',
  SIGNATURE: 'signature',
  CLOCK_OUT: 'clock_out',
  COMPLETE: 'complete'
};

const JobWorkflowModal = ({ 
  isOpen, 
  onClose, 
  booking, 
  worker,
  onWorkflowComplete 
}) => {
  // Determine initial step - cash payments need verification first
  const getInitialStep = useCallback(() => {
    // Cash payment that hasn't been verified yet
    if (booking?.payment_method === 'cash' && 
        booking?.payment_status === 'cash_pending' &&
        !booking?.cash_verified_at) {
      return WORKFLOW_STEPS.CASH_VERIFY;
    }
    return WORKFLOW_STEPS.CLOCK_IN;
  }, [booking]);

  // Workflow state
  const [currentStep, setCurrentStep] = useState(WORKFLOW_STEPS.CLOCK_IN);
  const [loading, setLoading] = useState(false);
  
  // Cash verification state
  const [cashVerificationNotes, setCashVerificationNotes] = useState('');
  const [cashVerified, setCashVerified] = useState(false);
  
  // GPS state
  const [gpsStatus, setGpsStatus] = useState('checking'); // checking, success, failed, manual
  const [workerLocation, setWorkerLocation] = useState(null);
  const [distanceFromJob, setDistanceFromJob] = useState(null);
  
  // Time tracking
  const [clockInTime, setClockInTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const timerRef = useRef(null);
  
  // Checklist state
  const [checklist, setChecklist] = useState([]);
  const [checklistComplete, setChecklistComplete] = useState(false);
  
  // Photos state
  const [photos, setPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);
  
  // Notes state
  const [notes, setNotes] = useState('');
  const [reminders, setReminders] = useState('');
  
  // Signature state
  const [signatureType, setSignatureType] = useState('customer'); // customer, worker
  const [signatureData, setSignatureData] = useState(null);
  const [signerName, setSignerName] = useState('');
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  
  // Clock out state
  const [clockOutTime, setClockOutTime] = useState(null);
  const [totalTime, setTotalTime] = useState(null);

  // Generate checklist from booking details - Enhanced service-specific checklists
  const generateChecklist = useCallback(() => {
    const items = [];
    const serviceType = booking?.service_type?.toLowerCase() || '';
    const serviceName = booking?.service_name?.toLowerCase() || '';
    
    // CLEANING SERVICE - Detailed checklist
    if (serviceType === 'cleaning' || serviceName.includes('clean')) {
      // Standard cleaning items
      items.push(
        { id: 'arrive_clean', label: '🚗 Arrive at location & greet customer', checked: false },
        { id: 'walkthrough', label: '👀 Do walkthrough with customer (if present)', checked: false },
        { id: 'dust', label: '🧹 Dust all surfaces, fans, blinds', checked: false },
        { id: 'vacuum', label: '🔌 Vacuum all carpets & rugs', checked: false },
        { id: 'mop', label: '🧽 Mop hard floors', checked: false },
        { id: 'bathrooms', label: '🚿 Clean & sanitize bathrooms (toilets, sinks, mirrors)', checked: false },
        { id: 'kitchen', label: '🍳 Clean kitchen (counters, appliances exterior, sink)', checked: false },
        { id: 'trash', label: '🗑️ Empty all trash bins', checked: false },
        { id: 'beds', label: '🛏️ Make beds / change linens (if requested)', checked: false },
        { id: 'wipe_surfaces', label: '✨ Wipe down light switches & door handles', checked: false },
        { id: 'final_walkthrough', label: '✅ Final walkthrough & quality check', checked: false }
      );
      
      // Deep clean additions
      if (serviceName.includes('deep')) {
        items.push(
          { id: 'baseboards', label: '📐 Clean baseboards', checked: false },
          { id: 'inside_appliances', label: '🧊 Clean inside refrigerator & oven', checked: false },
          { id: 'windows', label: '🪟 Clean interior windows', checked: false },
          { id: 'cabinets', label: '🗄️ Wipe cabinet exteriors', checked: false }
        );
      }
      
    // LAUNDRY SERVICE - Detailed checklist  
    } else if (serviceType === 'laundry' || serviceName.includes('laundry')) {
      items.push(
        { id: 'collect', label: '🧺 Collect all laundry items from customer', checked: false },
        { id: 'count', label: '📝 Count & document items (prevent loss claims)', checked: false },
        { id: 'check_pockets', label: '👖 Check all pockets', checked: false },
        { id: 'sort', label: '🎨 Sort by color (whites, darks, colors)', checked: false },
        { id: 'sort_fabric', label: '👕 Sort by fabric type (delicates separate)', checked: false },
        { id: 'pretreat', label: '🧪 Pre-treat any stains', checked: false },
        { id: 'wash', label: '🌀 Wash items (appropriate settings)', checked: false },
        { id: 'dry', label: '🔥 Dry items (check labels for settings)', checked: false },
        { id: 'fold', label: '👔 Fold clothes neatly', checked: false },
        { id: 'hang', label: '👗 Hang items that require it', checked: false },
        { id: 'package', label: '📦 Package items for return', checked: false },
        { id: 'deliver', label: '🚗 Return items to customer', checked: false }
      );
      
    // COURIER SERVICE - Detailed checklist
    } else if (serviceType === 'courier' || serviceName.includes('courier') || serviceName.includes('delivery')) {
      items.push(
        { id: 'pickup_location', label: '📍 Arrive at pickup location', checked: false },
        { id: 'verify_sender', label: '🆔 Verify sender identity', checked: false },
        { id: 'inspect_package', label: '📦 Inspect package condition (note any damage)', checked: false },
        { id: 'verify_contents', label: '📋 Verify package contents with sender', checked: false },
        { id: 'photo_pickup', label: '📸 Take photo of package at pickup', checked: false },
        { id: 'secure_package', label: '🔒 Secure package for transport', checked: false },
        { id: 'transport', label: '🚗 Transport to destination', checked: false },
        { id: 'arrive_destination', label: '📍 Arrive at delivery location', checked: false },
        { id: 'verify_recipient', label: '🆔 Verify recipient identity', checked: false },
        { id: 'deliver_package', label: '📬 Hand over package', checked: false },
        { id: 'photo_delivery', label: '📸 Take photo of delivery confirmation', checked: false },
        { id: 'get_signature', label: '✍️ Get recipient signature', checked: false }
      );
      
    // ERRAND SERVICE - Detailed checklist
    } else if (serviceType === 'errands' || serviceName.includes('errand')) {
      items.push(
        { id: 'review_list', label: '📋 Review errand list with customer', checked: false },
        { id: 'confirm_budget', label: '💰 Confirm budget & payment method', checked: false },
        { id: 'get_items', label: '🔑 Get any needed keys, cards, or access', checked: false },
        { id: 'shopping', label: '🛒 Complete shopping tasks', checked: false },
        { id: 'pickup_items', label: '📦 Pick up any pre-ordered items', checked: false },
        { id: 'bank_post', label: '🏦 Complete bank/post office errands', checked: false },
        { id: 'collect_receipts', label: '🧾 Collect ALL receipts', checked: false },
        { id: 'verify_items', label: '✅ Verify all items against list', checked: false },
        { id: 'deliver_items', label: '🚗 Deliver items to customer', checked: false },
        { id: 'return_items', label: '🔑 Return keys/cards to customer', checked: false },
        { id: 'provide_receipts', label: '🧾 Provide all receipts & change', checked: false }
      );
      
    // TASKS SERVICE - General task checklist
    } else if (serviceType === 'tasks' || serviceName.includes('task')) {
      items.push(
        { id: 'arrive', label: '🚗 Arrive at location', checked: false },
        { id: 'greet', label: '👋 Greet customer & review task', checked: false },
        { id: 'assess', label: '🔍 Assess work area & requirements', checked: false },
        { id: 'gather_tools', label: '🧰 Gather necessary tools/supplies', checked: false },
        { id: 'complete_task', label: '⚡ Complete main task', checked: false },
        { id: 'quality_check', label: '✅ Quality check work', checked: false },
        { id: 'cleanup', label: '🧹 Clean up work area', checked: false },
        { id: 'walkthrough', label: '👀 Walkthrough with customer', checked: false }
      );
      
    // DEFAULT - Generic checklist
    } else {
      items.push(
        { id: 'arrive', label: '🚗 Arrive at location', checked: false },
        { id: 'greet', label: '👋 Greet customer', checked: false },
        { id: 'review', label: '📋 Review job requirements', checked: false },
        { id: 'complete', label: '⚡ Complete main task', checked: false },
        { id: 'quality', label: '✅ Quality check', checked: false },
        { id: 'cleanup', label: '🧹 Clean up work area', checked: false }
      );
    }
    
    // Add custom items from booking notes
    if (booking?.special_instructions) {
      items.push({ 
        id: 'special', 
        label: `⚠️ Special: ${booking.special_instructions.substring(0, 50)}${booking.special_instructions.length > 50 ? '...' : ''}`, 
        checked: false 
      });
    }
    
    // Add add-on items
    if (booking?.add_ons?.length > 0) {
      booking.add_ons.forEach((addon, idx) => {
        items.push({ 
          id: `addon_${idx}`, 
          label: `➕ Add-on: ${addon.name || addon}`, 
          checked: false 
        });
      });
    }
    
    // Add selected_addons items (alternative field name)
    if (booking?.selected_addons?.length > 0) {
      booking.selected_addons.forEach((addon, idx) => {
        if (!booking?.add_ons?.some(a => (a.name || a) === (addon.name || addon.add_on_name))) {
          items.push({ 
            id: `selected_addon_${idx}`, 
            label: `➕ Add-on: ${addon.name || addon.add_on_name || addon}`, 
            checked: false 
          });
        }
      });
    }
    
    setChecklist(items);
  }, [booking]);

  // Check GPS location
  const checkGPSLocation = useCallback(async () => {
    setGpsStatus('checking');
    
    if (!navigator.geolocation) {
      setGpsStatus('manual');
      toast.error('GPS not available on this device');
      return;
    }
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setWorkerLocation({ lat: latitude, lng: longitude });
        
        // Calculate distance from job location (if available)
        if (booking?.address_lat && booking?.address_lng) {
          const distance = calculateDistance(
            latitude, longitude,
            booking.address_lat, booking.address_lng
          );
          setDistanceFromJob(distance);
          
          // Allow clock in if within 500 meters
          if (distance <= 0.5) {
            setGpsStatus('success');
          } else {
            setGpsStatus('failed');
            toast.warning(`You are ${distance.toFixed(2)} km from the job location`);
          }
        } else {
          // No job coordinates, allow manual clock in
          setGpsStatus('success');
        }
      },
      (error) => {
        console.error('GPS error:', error);
        setGpsStatus('manual');
        toast.error('Could not get your location. Manual clock-in available.');
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, [booking]);

  // Calculate distance between two coordinates (Haversine formula)
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  // Handle clock in
  const handleClockIn = async () => {
    setLoading(true);
    try {
      const response = await api.post('/job-workflow/clock-in', {
        booking_id: booking.id,
        location: workerLocation,
        gps_verified: gpsStatus === 'success'
      });
      
      setClockInTime(new Date());
      generateChecklist();
      setCurrentStep(WORKFLOW_STEPS.WORK_DETAILS);
      toast.success('Clocked in successfully!');
      
      // Start timer
      timerRef.current = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clock in');
    } finally {
      setLoading(false);
    }
  };

  // Handle checklist toggle
  const toggleChecklistItem = (itemId) => {
    setChecklist(prev => {
      const updated = prev.map(item => 
        item.id === itemId ? { ...item, checked: !item.checked } : item
      );
      // Check if all items are complete
      const allComplete = updated.every(item => item.checked);
      setChecklistComplete(allComplete);
      return updated;
    });
  };

  // Handle photo upload
  const handlePhotoCapture = async (event) => {
    const files = event.target.files;
    if (!files?.length) return;
    
    setUploadingPhoto(true);
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('booking_id', booking.id);
        formData.append('photo_type', 'completion');
        
        const response = await api.post('/job-workflow/photos/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        setPhotos(prev => [...prev, {
          id: response.data.photo_id || Date.now(),
          url: response.data.url || URL.createObjectURL(file),
          uploaded: true
        }]);
      } catch (error) {
        toast.error('Failed to upload photo');
      }
    }
    
    setUploadingPhoto(false);
  };

  // Signature canvas functions
  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#D4AF37';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    if (canvasRef.current) {
      setSignatureData(canvasRef.current.toDataURL());
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSignatureData(null);
  };

  // Handle job completion
  const handleCompleteJob = async () => {
    setLoading(true);
    try {
      // First, upload photos and signature to job-completion API (if any)
      try {
        const token = localStorage.getItem('auth_token');
        if (token && (photos.length > 0 || signatureData)) {
          const completionData = {
            job_id: booking.id,
            service_type: booking.service_type || 'errands',
            photos: photos.map(p => ({
              photo_data: p.data || p.url,
              photo_type: 'proof',
              caption: 'Job completion photo'
            })),
            signature_data: signatureData,
            signer_name: signerName,
            completion_notes: notes,
            customer_satisfied: true,
            checklist_completed: checklist.filter(c => c.checked).length,
            checklist_total: checklist.length,
            start_time: clockInTime?.toISOString(),
            end_time: new Date().toISOString()
          };
          
          await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/job-completion/complete`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(completionData)
          });
        }
      } catch (uploadErr) {
        console.warn('Photo/signature upload to job-completion failed, continuing:', uploadErr);
      }
      
      // Complete job via job-workflow endpoint (handles clock out, notification, and payroll automatically)
      const response = await api.post('/job-workflow/complete', {
        booking_id: booking.id,
        checklist: checklist,
        photos: photos.map(p => p.url),
        notes: notes,
        reminders: reminders,
        signature: {
          type: signatureType,
          signer_name: signerName,
          data: signatureData
        },
        clock_in_time: clockInTime?.toISOString(),
        clock_out_time: new Date().toISOString()
      });
      
      setClockOutTime(new Date());
      setTotalTime(response.data.duration_minutes || Math.floor(elapsedTime / 60));
      
      toast.success('Job completed successfully!');
      setCurrentStep(WORKFLOW_STEPS.COMPLETE);
      
      // Stop timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete job');
    } finally {
      setLoading(false);
    }
  };

  // Format elapsed time
  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle cash verification
  const handleCashVerification = async (received) => {
    setLoading(true);
    try {
      const response = await api.post(`/bookings/${booking.id}/verify-cash-payment`, {
        received: received,
        notes: cashVerificationNotes
      });
      
      if (received) {
        setCashVerified(true);
        toast.success('Cash payment verified! Proceeding to clock in.');
        setCurrentStep(WORKFLOW_STEPS.CLOCK_IN);
        // Start GPS check for clock-in
        checkGPSLocation();
      } else {
        toast.error('Booking cancelled due to cash not received.');
        onWorkflowComplete?.({ cancelled: true, reason: 'cash_not_received' });
        onClose();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify cash payment');
    } finally {
      setLoading(false);
    }
  };

  // Initialize GPS check when modal opens and set correct initial step
  useEffect(() => {
    if (isOpen) {
      const initialStep = getInitialStep();
      setCurrentStep(initialStep);
      
      if (initialStep === WORKFLOW_STEPS.CLOCK_IN) {
        checkGPSLocation();
      }
    }
  }, [isOpen, getInitialStep, checkGPSLocation]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  if (!isOpen || !booking) return null;

  // For courier jobs, use the dedicated CourierWorkflowSteps component
  const isCourierJob = booking.service_type === 'courier' || 
                       booking.service_name?.toLowerCase().includes('courier') ||
                       booking.service_name?.toLowerCase().includes('delivery');

  if (isCourierJob && booking.courier_info) {
    return (
      <CourierWorkflowSteps
        booking={booking}
        worker={worker}
        onComplete={() => {
          onWorkflowComplete?.();
          onClose();
        }}
        onClose={onClose}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-black/90 z-50 overflow-y-auto">
      <div className="min-h-screen p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-lg mx-auto bg-[var(--background-paper)] rounded-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#D4AF37]/20 to-[#D4AF37]/10 p-4 border-b border-[var(--border-subtle)]">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold">{booking.service_name}</h2>
                <p className="text-sm text-[var(--text-muted)]">{booking.customer_name}</p>
              </div>
              {currentStep !== WORKFLOW_STEPS.COMPLETE && (
                <button onClick={onClose} className="p-2 rounded-lg hover:bg-[var(--background-elevated)]">
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
            
            {/* Timer (shown during work) */}
            {clockInTime && !clockOutTime && (
              <div className="mt-3 flex items-center gap-2 text-[#D4AF37]">
                <Timer className="w-5 h-5" />
                <span className="text-xl font-mono font-bold">{formatTime(elapsedTime)}</span>
              </div>
            )}
          </div>

          {/* Step Content */}
          <div className="p-4">
            {/* STEP: Cash Payment Verification (for cash payments only) */}
            {currentStep === WORKFLOW_STEPS.CASH_VERIFY && (
              <div className="space-y-6">
                <div className="text-center">
                  <div className="w-20 h-20 mx-auto rounded-full bg-[#D4AF37]/20 flex items-center justify-center mb-4">
                    <Banknote className="w-10 h-10 text-[#D4AF37]" />
                  </div>
                  <h3 className="text-xl font-bold">Cash Payment Verification</h3>
                  <p className="text-[var(--text-muted)] mt-2">
                    This booking requires cash payment. Verify payment before starting work.
                  </p>
                </div>
                
                {/* Payment Amount Display */}
                <div className="bg-[var(--background-elevated)] rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[var(--text-muted)]">Amount Due</span>
                    <span className="text-2xl font-bold text-[#D4AF37]">
                      ${(booking.total_amount || booking.total_price || 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="text-sm text-[var(--text-muted)] space-y-1">
                    <p><strong>Service:</strong> {booking.service_name}</p>
                    <p><strong>Customer:</strong> {booking.customer_name}</p>
                    {booking.customer_phone && (
                      <p><strong>Phone:</strong> {booking.customer_phone}</p>
                    )}
                  </div>
                </div>
                
                {/* Warning Notice */}
                <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-orange-500">Important</p>
                      <p className="text-[var(--text-muted)]">
                        Collect the full cash payment BEFORE starting any work. 
                        If cash is not received, the booking will be cancelled.
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Notes Input */}
                <div>
                  <Label>Verification Notes (optional)</Label>
                  <Textarea
                    value={cashVerificationNotes}
                    onChange={(e) => setCashVerificationNotes(e.target.value)}
                    placeholder="Any notes about the payment..."
                    rows={2}
                    className="mt-1"
                  />
                </div>
                
                {/* Action Buttons */}
                <div className="space-y-3">
                  <Button 
                    onClick={() => handleCashVerification(true)}
                    disabled={loading}
                    className="w-full bg-green-500 hover:bg-green-600 text-white py-4"
                    data-testid="cash-received-btn"
                  >
                    {loading ? (
                      <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Processing...</>
                    ) : (
                      <><CheckCircle className="w-5 h-5 mr-2" /> Cash Received - Start Job</>
                    )}
                  </Button>
                  
                  <Button 
                    onClick={() => handleCashVerification(false)}
                    disabled={loading}
                    variant="outline"
                    className="w-full border-red-500 text-red-500 hover:bg-red-500/10 py-4"
                    data-testid="cash-not-received-btn"
                  >
                    <XCircle className="w-5 h-5 mr-2" /> Cash Not Received - Cancel Booking
                  </Button>
                  
                  <Button 
                    variant="ghost" 
                    onClick={onClose}
                    className="w-full"
                  >
                    Go Back
                  </Button>
                </div>
              </div>
            )}

            {/* STEP: Clock In Prompt */}
            {currentStep === WORKFLOW_STEPS.CLOCK_IN && (
              <div className="space-y-6 text-center">
                <div className="w-20 h-20 mx-auto rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                  <Clock className="w-10 h-10 text-[#D4AF37]" />
                </div>
                
                <div>
                  <h3 className="text-xl font-bold">Ready to Start?</h3>
                  <p className="text-[var(--text-muted)] mt-2">Would you like to clock in for this job?</p>
                </div>
                
                {/* Job Address */}
                <div className="bg-[var(--background-elevated)] rounded-xl p-4 text-left">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-[#D4AF37] mt-0.5" />
                    <div>
                      <p className="font-medium">{booking.address || 'Address not provided'}</p>
                      <p className="text-sm text-[var(--text-muted)]">
                        {new Date(booking.booking_date).toLocaleDateString()} at {booking.booking_time}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* GPS Status */}
                <div className="bg-[var(--background-elevated)] rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Navigation className="w-5 h-5" />
                      <span>GPS Verification</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {gpsStatus === 'checking' && (
                        <><Loader2 className="w-4 h-4 animate-spin" /><span className="text-sm">Checking...</span></>
                      )}
                      {gpsStatus === 'success' && (
                        <><CheckCircle className="w-4 h-4 text-green-500" /><span className="text-sm text-green-500">Verified</span></>
                      )}
                      {gpsStatus === 'failed' && (
                        <><AlertTriangle className="w-4 h-4 text-orange-500" /><span className="text-sm text-orange-500">{distanceFromJob?.toFixed(1)} km away</span></>
                      )}
                      {gpsStatus === 'manual' && (
                        <><Info className="w-4 h-4 text-blue-500" /><span className="text-sm text-blue-500">Manual</span></>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-3">
                  <Button variant="outline" onClick={onClose} className="flex-1">
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleClockIn}
                    disabled={loading || gpsStatus === 'checking'}
                    className="flex-1 bg-[#D4AF37] hover:bg-[#B8860B] text-black"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Clock className="w-4 h-4 mr-2" />}
                    Clock In
                  </Button>
                </div>
                
                {gpsStatus === 'failed' && (
                  <p className="text-xs text-orange-500">
                    You can still clock in, but GPS verification failed. This will be noted.
                  </p>
                )}
              </div>
            )}

            {/* STEP: Work Details & Checklist */}
            {currentStep === WORKFLOW_STEPS.WORK_DETAILS && (
              <div className="space-y-4">
                <h3 className="text-lg font-bold flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-[#D4AF37]" />
                  Job Details
                </h3>
                
                {/* Access Info */}
                <div className="bg-[var(--background-elevated)] rounded-xl p-4 space-y-3">
                  <h4 className="font-medium flex items-center gap-2">
                    <Key className="w-4 h-4 text-[#D4AF37]" />
                    Access Information
                  </h4>
                  
                  {booking.access_instructions ? (
                    <p className="text-sm">{booking.access_instructions}</p>
                  ) : (
                    <p className="text-sm text-[var(--text-muted)]">No specific access instructions</p>
                  )}
                  
                  <div className="flex flex-wrap gap-2 text-sm">
                    {booking.key_location && (
                      <span className="px-2 py-1 bg-[#D4AF37]/20 rounded-lg flex items-center gap-1">
                        <Key className="w-3 h-3" /> {booking.key_location}
                      </span>
                    )}
                    {booking.door_code && (
                      <span className="px-2 py-1 bg-[#D4AF37]/20 rounded-lg flex items-center gap-1">
                        <Lock className="w-3 h-3" /> Code: {booking.door_code}
                      </span>
                    )}
                    {booking.someone_home && (
                      <span className="px-2 py-1 bg-green-500/20 text-green-500 rounded-lg flex items-center gap-1">
                        <Users className="w-3 h-3" /> Someone will be home
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Customer Contact */}
                <div className="bg-[var(--background-elevated)] rounded-xl p-4">
                  <h4 className="font-medium flex items-center gap-2 mb-2">
                    <User className="w-4 h-4 text-[#D4AF37]" />
                    Customer
                  </h4>
                  <p className="font-medium">{booking.customer_name}</p>
                  {booking.customer_phone && (
                    <div className="flex gap-2 mt-2">
                      <a 
                        href={`tel:${booking.customer_phone}`} 
                        className="flex-1 px-3 py-2 bg-green-500/20 text-green-500 rounded-lg flex items-center justify-center gap-2 hover:bg-green-500/30 transition-all"
                        data-testid="call-customer-btn"
                      >
                        <Phone className="w-4 h-4" /> Call
                      </a>
                      <a 
                        href={`sms:${booking.customer_phone}`} 
                        className="flex-1 px-3 py-2 bg-blue-500/20 text-blue-500 rounded-lg flex items-center justify-center gap-2 hover:bg-blue-500/30 transition-all"
                        data-testid="text-customer-btn"
                      >
                        <MessageSquare className="w-4 h-4" /> Text
                      </a>
                    </div>
                  )}
                </div>
                
                {/* Special Instructions */}
                {booking.special_instructions && (
                  <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4">
                    <h4 className="font-medium flex items-center gap-2 text-orange-500 mb-2">
                      <AlertTriangle className="w-4 h-4" />
                      Special Instructions
                    </h4>
                    <p className="text-sm">{booking.special_instructions}</p>
                  </div>
                )}
                
                {/* Checklist */}
                <div className="bg-[var(--background-elevated)] rounded-xl p-4">
                  <h4 className="font-medium flex items-center gap-2 mb-3">
                    <CheckSquare className="w-4 h-4 text-[#D4AF37]" />
                    Task Checklist
                  </h4>
                  
                  <div className="space-y-2">
                    {checklist.map(item => (
                      <button
                        key={item.id}
                        onClick={() => toggleChecklistItem(item.id)}
                        className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all ${
                          item.checked 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-[var(--background-paper)] hover:bg-[var(--background-subtle)]'
                        }`}
                      >
                        {item.checked ? (
                          <CheckSquare className="w-5 h-5 text-green-500" />
                        ) : (
                          <Square className="w-5 h-5" />
                        )}
                        <span className={item.checked ? 'line-through' : ''}>{item.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
                
                <Button 
                  onClick={() => setCurrentStep(WORKFLOW_STEPS.COMPLETION)}
                  className="w-full bg-[#D4AF37] hover:bg-[#B8860B] text-black"
                >
                  {checklistComplete ? 'All Tasks Complete - Continue' : 'Continue to Completion'}
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}

            {/* STEP: Completion Confirmation */}
            {currentStep === WORKFLOW_STEPS.COMPLETION && (
              <div className="space-y-6 text-center">
                <div className="w-20 h-20 mx-auto rounded-full bg-green-500/20 flex items-center justify-center">
                  <Check className="w-10 h-10 text-green-500" />
                </div>
                
                <div>
                  <h3 className="text-xl font-bold">Job Complete?</h3>
                  <p className="text-[var(--text-muted)] mt-2">
                    Have you finished all tasks for this job?
                  </p>
                </div>
                
                {/* Checklist Summary */}
                <div className="bg-[var(--background-elevated)] rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <span>Tasks Completed</span>
                    <span className="font-bold text-[#D4AF37]">
                      {checklist.filter(i => i.checked).length} / {checklist.length}
                    </span>
                  </div>
                </div>
                
                {!checklistComplete && (
                  <p className="text-sm text-orange-500">
                    Some tasks are not checked. Are you sure you want to continue?
                  </p>
                )}
                
                <div className="flex gap-3">
                  <Button 
                    variant="outline" 
                    onClick={() => setCurrentStep(WORKFLOW_STEPS.WORK_DETAILS)}
                    className="flex-1"
                  >
                    Go Back
                  </Button>
                  <Button 
                    onClick={() => setCurrentStep(WORKFLOW_STEPS.PHOTOS)}
                    className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                  >
                    Yes, Continue
                  </Button>
                </div>
              </div>
            )}

            {/* STEP: Before/After Photos Upload */}
            {currentStep === WORKFLOW_STEPS.PHOTOS && (
              <div className="space-y-4">
                <BeforeAfterPhotoUpload
                  bookingId={booking?.id}
                  serviceType={booking?.service_type}
                  initialPhotos={{ 
                    before: photos.filter(p => p.type === 'before'),
                    after: photos.filter(p => p.type === 'after' || !p.type)
                  }}
                  onComplete={(photoData) => {
                    // Merge photos with types
                    const allPhotos = [
                      ...photoData.before.map(p => ({ ...p, type: 'before' })),
                      ...photoData.after.map(p => ({ ...p, type: 'after' }))
                    ];
                    setPhotos(allPhotos);
                    setCurrentStep(WORKFLOW_STEPS.NOTES);
                  }}
                />
              </div>
            )}

            {/* STEP: Notes & Reminders */}
            {currentStep === WORKFLOW_STEPS.NOTES && (
              <div className="space-y-4">
                <h3 className="text-lg font-bold flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-[#D4AF37]" />
                  Notes & Reminders
                </h3>
                
                <div>
                  <Label>Notes for Customer</Label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Any notes about the completed work..."
                    rows={3}
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label>Reminders / Follow-up</Label>
                  <Textarea
                    value={reminders}
                    onChange={(e) => setReminders(e.target.value)}
                    placeholder="Any reminders or recommendations..."
                    rows={3}
                    className="mt-1"
                  />
                </div>
                
                <Button 
                  onClick={() => setCurrentStep(WORKFLOW_STEPS.SIGNATURE)}
                  className="w-full bg-[#D4AF37] hover:bg-[#B8860B] text-black"
                >
                  Continue to Signature
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}

            {/* STEP: Signature */}
            {currentStep === WORKFLOW_STEPS.SIGNATURE && (
              <div className="space-y-4">
                <h3 className="text-lg font-bold flex items-center gap-2">
                  <Pen className="w-5 h-5 text-[#D4AF37]" />
                  Signature
                </h3>
                
                {/* Signature Type */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setSignatureType('customer')}
                    className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                      signatureType === 'customer' 
                        ? 'border-[#D4AF37] bg-[#D4AF37]/10' 
                        : 'border-[var(--border-subtle)]'
                    }`}
                  >
                    <User className="w-5 h-5 mx-auto mb-1" />
                    <span className="text-sm">Customer Signs</span>
                  </button>
                  <button
                    onClick={() => setSignatureType('worker')}
                    className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                      signatureType === 'worker' 
                        ? 'border-[#D4AF37] bg-[#D4AF37]/10' 
                        : 'border-[var(--border-subtle)]'
                    }`}
                  >
                    <AlertTriangle className="w-5 h-5 mx-auto mb-1" />
                    <span className="text-sm">Customer Unavailable</span>
                  </button>
                </div>
                
                {signatureType === 'worker' && (
                  <p className="text-sm text-orange-500 bg-orange-500/10 p-3 rounded-lg">
                    Customer was not available to sign. Worker signature will be used.
                  </p>
                )}
                
                {/* Signer Name */}
                <div>
                  <Label>{signatureType === 'customer' ? 'Customer Name' : 'Worker Name'}</Label>
                  <Input
                    value={signerName}
                    onChange={(e) => setSignerName(e.target.value)}
                    placeholder="Enter name"
                    className="mt-1"
                  />
                </div>
                
                {/* Signature Canvas */}
                <div>
                  <Label>Signature</Label>
                  <div className="mt-1 bg-[var(--background-elevated)] rounded-xl p-2">
                    <canvas
                      ref={canvasRef}
                      width={300}
                      height={150}
                      onMouseDown={startDrawing}
                      onMouseMove={draw}
                      onMouseUp={stopDrawing}
                      onMouseLeave={stopDrawing}
                      onTouchStart={startDrawing}
                      onTouchMove={draw}
                      onTouchEnd={stopDrawing}
                      className="w-full border border-[var(--border-subtle)] rounded-lg bg-white cursor-crosshair touch-none"
                    />
                    <button
                      onClick={clearSignature}
                      className="mt-2 text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                    >
                      Clear Signature
                    </button>
                  </div>
                </div>
                
                <Button 
                  onClick={handleCompleteJob}
                  disabled={loading || !signerName || !signatureData}
                  className="w-full bg-green-500 hover:bg-green-600 text-white"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Processing...</>
                  ) : (
                    <><Check className="w-4 h-4 mr-2" /> Complete & Clock Out</>
                  )}
                </Button>
              </div>
            )}

            {/* STEP: Complete */}
            {currentStep === WORKFLOW_STEPS.COMPLETE && (
              <div className="space-y-6 text-center py-6">
                <motion.div 
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="w-24 h-24 mx-auto rounded-full bg-green-500 flex items-center justify-center"
                >
                  <CheckCircle className="w-12 h-12 text-white" />
                </motion.div>
                
                <div>
                  <h3 className="text-2xl font-bold text-green-500">Job Complete!</h3>
                  <p className="text-[var(--text-muted)] mt-2">
                    Great work! The job has been completed and sent to the system.
                  </p>
                </div>
                
                {/* Summary */}
                <div className="bg-[var(--background-elevated)] rounded-xl p-4 space-y-3 text-left">
                  <div className="flex justify-between">
                    <span className="text-[var(--text-muted)]">Total Time</span>
                    <span className="font-bold">{formatTime(elapsedTime)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[var(--text-muted)]">Tasks Completed</span>
                    <span className="font-bold">{checklist.filter(i => i.checked).length} / {checklist.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[var(--text-muted)]">Photos Uploaded</span>
                    <span className="font-bold">{photos.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[var(--text-muted)]">Signature</span>
                    <span className="font-bold text-green-500">Captured</span>
                  </div>
                </div>
                
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 text-sm">
                  <p>Photos and notes have been automatically sent to:</p>
                  <ul className="mt-2 space-y-1 text-left">
                    <li>✓ Customer email</li>
                    <li>✓ Customer dashboard</li>
                    <li>✓ Owner dashboard</li>
                    <li>✓ Payroll system</li>
                  </ul>
                </div>
                
                {/* Download Signature PDF Button */}
                <Button 
                  onClick={async () => {
                    try {
                      const response = await api.get(`/job-workflow/signature/${booking.id}/pdf`, { responseType: 'blob' });
                      const blob = new Blob([response.data], { type: 'application/pdf' });
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `signature_${booking.service_name?.replace(/\s+/g, '_') || 'job'}.pdf`;
                      document.body.appendChild(a);
                      a.click();
                      window.URL.revokeObjectURL(url);
                      a.remove();
                      toast.success('Signature PDF downloaded');
                    } catch (error) {
                      toast.error('Failed to download signature PDF');
                    }
                  }}
                  variant="outline"
                  className="w-full border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10"
                  data-testid="download-signature-pdf-btn"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Download Signature PDF
                </Button>
                
                <Button 
                  onClick={() => {
                    onWorkflowComplete?.();
                    onClose();
                  }}
                  className="w-full bg-[#D4AF37] hover:bg-[#B8860B] text-black"
                >
                  Done
                </Button>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default JobWorkflowModal;
